import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";
import CategoryRuleSelector from "./CategoryRuleSelector";
import { formatPrice } from "@/utils/formatPrice";
import type { CustomerCategory } from "@/hooks/useCustomerCategories";
import { type CancellationTier, legacyToTiers, generateTierPreview } from "@/lib/cancellationFee";

interface Props {
  settings: Record<string, unknown>;
  updateSetting: (key: string, value: unknown) => void;
  hall: Record<string, unknown> | null;
  updateHall: (field: string, value: unknown) => void;
  categories?: CustomerCategory[];
  onUpdateCategoryRules?: (id: string, rules: Record<string, unknown> | null) => void;
}

export default function SettingsCancellation({ settings, updateSetting, hall, updateHall: _updateHall, categories = [], onUpdateCategoryRules }: Props) {
  const { t } = useTranslation("admin");
  const [selectedCatId, setSelectedCatId] = useState("global");
  const cancellation = (settings.cancellation as Record<string, unknown>) || {};
  const selectedCat = categories.find((c) => c.id === selectedCatId);
  const catRules = selectedCat?.cancellation_rules as Record<string, unknown> | null;
  const isOverride = selectedCatId !== "global";

  const update = (key: string, value: unknown) => {
    if (isOverride && selectedCat && onUpdateCategoryRules) {
      const current = catRules || {};
      onUpdateCategoryRules(selectedCat.id, { ...current, [key]: value });
    } else {
      updateSetting("cancellation", { ...cancellation, [key]: value });
    }
  };

  const getValue = (key: string, defaultVal: any) => {
    if (isOverride && catRules && catRules[key] !== undefined) return catRules[key];
    return cancellation[key] ?? defaultVal;
  };

  // Tiers state — load from settings or convert legacy
  const initialTiers: CancellationTier[] = useMemo(() => {
    const saved = getValue("tiers", null) as CancellationTier[] | null;
    if (saved && Array.isArray(saved) && saved.length > 0) return saved;
    return legacyToTiers(hall?.cancellation_hours, cancellation.late_cancel_fee);
  }, [selectedCatId]);

  const [tiers, setTiers] = useState<CancellationTier[]>(initialTiers);

  // Sync tiers back to settings whenever they change
  const updateTiers = (newTiers: CancellationTier[]) => {
    setTiers(newTiers);
    const sorted = [...newTiers].sort((a, b) => b.hours_before - a.hours_before);
    update("tiers", sorted);
  };

  const addTier = () => {
    const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
    const secondLast = sorted[sorted.length - 2]?.hours_before ?? 24;
    const newHours = Math.max(1, Math.floor(secondLast / 2));
    updateTiers([...tiers, { hours_before: newHours, percent: 50 }]);
  };

  const removeTier = (index: number) => {
    if (tiers.length <= 1) return;
    const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
    updateTiers(sorted.filter((_, i) => i !== index));
  };

  const updateTierField = (index: number, field: keyof CancellationTier, value: number) => {
    const sorted = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
    sorted[index] = { ...sorted[index], [field]: value };
    updateTiers(sorted);
  };

  const sortedTiers = [...tiers].sort((a, b) => b.hours_before - a.hours_before);
  const examplePrice = 550;
  const preview = generateTierPreview(tiers, examplePrice);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold mb-1">{t("settings.cancellation.title")}</h3>
          <p className="text-sm text-muted-foreground mb-4">{t("settings.cancellation.description")}</p>
        </div>
        {categories.length > 0 && (
          <CategoryRuleSelector categories={categories} selected={selectedCatId} onSelect={setSelectedCatId} />
        )}
      </div>

      {isOverride && (
        <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
          {t("settings.cancellation.overrideHint", { name: selectedCat?.name })}
        </p>
      )}

      {/* Tier editor */}
      <div className="space-y-3">
        <h4 className="font-medium text-sm">{t("settings.cancellation.tierTitle")}</h4>
        {sortedTiers.map((tier, index) => {
          const isLast = index === sortedTiers.length - 1;
          return (
            <div key={index} className="flex items-center gap-3 rounded-lg border p-3">
              <div className="flex-1 grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    {isLast ? t("settings.cancellation.underHours", { hours: sortedTiers[index - 1]?.hours_before ?? "?" }) : t("settings.cancellation.moreThanHours")}
                  </Label>
                  <NumberInput
                    min={0}
                    max={168}
                    value={tier.hours_before}
                    onValueChange={(v) => updateTierField(index, "hours_before", v || 0)}
                    disabled={isLast}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("settings.cancellation.feePercent")}</Label>
                  <NumberInput
                    min={0}
                    max={100}
                    value={tier.percent}
                    onValueChange={(v) => updateTierField(index, "percent", v || 0)}
                  />
                </div>
              </div>
              {!isLast && tiers.length > 1 && (
                <Button variant="ghost" size="icon" onClick={() => removeTier(index)} className="shrink-0">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
              {isLast && <div className="w-9 shrink-0" />}
            </div>
          );
        })}
        <Button variant="outline" size="sm" onClick={addTier} className="gap-1">
          <Plus className="h-3.5 w-3.5" /> {t("settings.cancellation.btnAddTier")}
        </Button>
      </div>

      {/* Preview */}
      <Card className="bg-muted/30">
        <CardContent className="p-4 space-y-2">
          <h4 className="font-medium text-sm">{t("settings.cancellation.previewTitle", { price: examplePrice })}</h4>
          {preview.map((p, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{p.label}</span>
              <span className={p.fee === 0 ? "text-green-600 font-medium" : "font-medium"}>
                {p.fee === 0 ? t("settings.cancellation.free") : `${p.fee} kr (${p.percent}%)`}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Resale refund */}
      {!isOverride && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label>{t("settings.cancellation.resaleCredit")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.cancellation.resaleCreditDescription")}
              </p>
            </div>
            <Switch
              checked={getValue("resale_refund_enabled", false)}
              onCheckedChange={(v) => update("resale_refund_enabled", v)}
            />
          </div>
          {getValue("resale_refund_enabled", false) && (
            <div className="space-y-2">
              <Label className="text-xs">{t("settings.cancellation.refundPercent")}</Label>
              <NumberInput
                min={0}
                max={100}
                value={getValue("resale_refund_percent", 80)}
                onValueChange={(v) => update("resale_refund_percent", v || 80)}
                className="w-32"
              />
              <p className="text-xs text-muted-foreground">
                Exempel: Spelare avbokar ({'<'}6h, {examplePrice} kr avgift). Banan bokas av annan.
                → Spelare får {formatPrice(Math.round(examplePrice * (getValue("resale_refund_percent", 80) / 100)))} spelkredit.
                Hallen behåller {formatPrice(examplePrice - Math.round(examplePrice * (getValue("resale_refund_percent", 80) / 100)))}.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Other toggles */}
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label>{t("settings.cancellation.allowLateCancellation")}</Label>
          <p className="text-xs text-muted-foreground">{t("settings.cancellation.allowLateCancellationHint")}</p>
        </div>
        <Switch checked={getValue("late_cancel_allowed", true)} onCheckedChange={(v) => update("late_cancel_allowed", v)} />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-4">
        <div>
          <Label>{t("settings.cancellation.chargeStandardPrice")}</Label>
          <p className="text-xs text-muted-foreground">
            {t("settings.cancellation.chargeStandardPriceHint")}
          </p>
        </div>
        <Switch checked={getValue("late_cancel_charge_standard", false)} onCheckedChange={(v) => update("late_cancel_charge_standard", v)} />
      </div>

      {/* No-show settings */}
      {!isOverride && (
        <NoShowSettings cancellation={cancellation} update={update} />
      )}
    </div>
  );
}

function NoShowSettings({ cancellation, update }: { cancellation: Record<string, unknown>; update: (key: string, value: unknown) => void }) {
  const { t } = useTranslation("admin");
  const noShow = (cancellation.no_show as Record<string, unknown>) || {};
  const setNoShow = (key: string, value: unknown) => {
    update("no_show", { ...noShow, [key]: value });
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div>
        <h4 className="font-medium">{t("settings.cancellation.noShowTitle")}</h4>
        <p className="text-xs text-muted-foreground">
          {t("settings.cancellation.noShowDescription")}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label>{t("settings.cancellation.enableNoShow")}</Label>
          <p className="text-xs text-muted-foreground">{t("settings.cancellation.enableNoShowHint")}</p>
        </div>
        <Switch checked={noShow.enabled ?? false} onCheckedChange={(v) => setNoShow("enabled", v)} />
      </div>

      {noShow.enabled && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs">{t("settings.cancellation.warningAfterCount")}</Label>
              <NumberInput min={1} value={noShow.warning_after ?? 2} onValueChange={(v) => setNoShow("warning_after", v)} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("settings.cancellation.banAfterCount")}</Label>
              <NumberInput min={1} value={noShow.ban_after ?? 3} onValueChange={(v) => setNoShow("ban_after", v)} />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs">{t("settings.cancellation.banDays")}</Label>
              <NumberInput min={1} value={noShow.ban_days ?? 7} onValueChange={(v) => setNoShow("ban_days", v)} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("settings.cancellation.windowDays")}</Label>
              <NumberInput min={1} value={noShow.window_days ?? 30} onValueChange={(v) => setNoShow("window_days", v)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
